import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Layers, Clock, Zap, Database, Users, Cpu } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { getTokenomics, getNetworkStats } from '../lib/api';
import { formatNumber } from '../lib/utils';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';

export default function TokenomicsPage() {
  const [tokenomics, setTokenomics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tokenRes, statsRes] = await Promise.all([
          getTokenomics(),
          getNetworkStats(),
        ]);
        setTokenomics(tokenRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching tokenomics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid gap-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  const supplyMined = stats ? ((stats.circulating_supply - 1200000) / 19800000) * 100 : 0;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="tokenomics-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-gradient-to-br from-[#00F0FF]/20 to-transparent">
            <TrendingUp className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">GGPAY Tokenomics</h1>
            <p className="text-sm text-muted-foreground">
              40-Year Mining Schedule with Hybrid PoW + PoS
            </p>
          </div>
        </div>

        {/* Supply Overview */}
        <div className="data-card p-6 mb-8">
          <h2 className="text-xl font-bold mb-6">Supply Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center p-6 rounded-xl bg-white/5">
              <p className="text-sm text-muted-foreground mb-2">Max Supply</p>
              <p className="text-3xl font-black gradient-text">{formatNumber(tokenomics.max_supply, 0)}</p>
              <p className="text-sm text-muted-foreground">GG</p>
            </div>
            <div className="text-center p-6 rounded-xl bg-white/5">
              <p className="text-sm text-muted-foreground mb-2">Genesis/Pre-Mint</p>
              <p className="text-3xl font-black text-yellow-400">{formatNumber(tokenomics.genesis_supply, 0)}</p>
              <p className="text-sm text-muted-foreground">GG</p>
            </div>
            <div className="text-center p-6 rounded-xl bg-white/5">
              <p className="text-sm text-muted-foreground mb-2">Mineable Supply</p>
              <p className="text-3xl font-black text-green-400">{formatNumber(tokenomics.mineable_supply, 0)}</p>
              <p className="text-sm text-muted-foreground">GG</p>
            </div>
          </div>
          
          <div className="mb-4">
            <div className="flex justify-between mb-2">
              <span className="text-sm text-muted-foreground">Mining Progress</span>
              <span className="text-sm font-mono">{supplyMined.toFixed(4)}%</span>
            </div>
            <Progress value={supplyMined} className="h-3" />
          </div>
        </div>

        {/* Genesis Allocation */}
        <div className="data-card p-6 mb-8">
          <h2 className="text-xl font-bold mb-6">Genesis Allocation (1,200,000 GG)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20">
              <Database className="w-6 h-6 text-blue-400 mb-2" />
              <p className="text-sm text-muted-foreground">Foundation Treasury</p>
              <p className="text-2xl font-bold">600,000 GG</p>
              <p className="text-xs text-muted-foreground">50%</p>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-transparent border border-green-500/20">
              <Cpu className="w-6 h-6 text-green-400 mb-2" />
              <p className="text-sm text-muted-foreground">Ecosystem & Dev</p>
              <p className="text-2xl font-bold">300,000 GG</p>
              <p className="text-xs text-muted-foreground">25%</p>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20">
              <TrendingUp className="w-6 h-6 text-purple-400 mb-2" />
              <p className="text-sm text-muted-foreground">Liquidity / Exchange</p>
              <p className="text-2xl font-bold">200,000 GG</p>
              <p className="text-xs text-muted-foreground">16.67%</p>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-yellow-500/10 to-transparent border border-yellow-500/20">
              <Users className="w-6 h-6 text-yellow-400 mb-2" />
              <p className="text-sm text-muted-foreground">Genesis Validators</p>
              <p className="text-2xl font-bold">100,000 GG</p>
              <p className="text-xs text-muted-foreground">8.33%</p>
            </div>
          </div>
        </div>

        {/* Network Parameters */}
        <div className="data-card p-6 mb-8">
          <h2 className="text-xl font-bold mb-6">Network Parameters</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-primary" />
                <p className="text-sm text-muted-foreground">Block Time</p>
              </div>
              <p className="text-2xl font-bold font-mono">10s</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-4 h-4 text-primary" />
                <p className="text-sm text-muted-foreground">Blocks/Year</p>
              </div>
              <p className="text-2xl font-bold font-mono">3,153,600</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-[#00F0FF]" />
                <p className="text-sm text-muted-foreground">PoW Share</p>
              </div>
              <p className="text-2xl font-bold font-mono text-[#00F0FF]">70%</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-[#7000FF]" />
                <p className="text-sm text-muted-foreground">PoS Share</p>
              </div>
              <p className="text-2xl font-bold font-mono text-[#7000FF]">30%</p>
            </div>
          </div>
        </div>

        {/* Epoch Rewards */}
        <div className="data-card overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h2 className="text-xl font-bold">40-Year Mining Reward Schedule</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Halving every 4 years • {formatNumber(tokenomics.blocks_per_epoch, 0)} blocks per epoch
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th>Epoch</th>
                  <th>Years</th>
                  <th>Total GG Minted</th>
                  <th>Reward/Block</th>
                  <th>PoW (70%)</th>
                  <th>PoS (30%)</th>
                </tr>
              </thead>
              <tbody>
                {tokenomics.epoch_rewards.map((epoch, i) => (
                  <tr 
                    key={i}
                    className={stats?.current_epoch === epoch.epoch ? 'bg-primary/10' : ''}
                  >
                    <td>
                      <span className="font-mono font-bold">{epoch.epoch}</span>
                      {stats?.current_epoch === epoch.epoch && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-primary/20 text-primary rounded">
                          Current
                        </span>
                      )}
                    </td>
                    <td className="font-mono">{epoch.years}</td>
                    <td className="font-mono">{formatNumber(epoch.total_gg, 0)} GG</td>
                    <td className="font-mono text-primary font-bold">{epoch.reward_per_block.toFixed(6)}</td>
                    <td className="font-mono text-[#00F0FF]">{epoch.pow.toFixed(6)}</td>
                    <td className="font-mono text-[#7000FF]">{epoch.pos.toFixed(6)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-white/10 bg-white/5">
                  <td colSpan={2} className="font-bold">Total</td>
                  <td className="font-mono font-bold text-green-400">{formatNumber(19800000, 0)} GG</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Post Mining Info */}
        <div className="data-card p-6 mt-8">
          <h2 className="text-xl font-bold mb-4">Post-Mining Phase (After Year 40)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span>No new GG minted - fixed & deflationary supply</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Miners & validators earn transaction fees only</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Network security sustained via fees & staking incentives</span>
                </li>
              </ul>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-[#00F0FF]/10 to-[#7000FF]/10 border border-white/10">
              <p className="text-sm text-muted-foreground mb-2">Economic Strength</p>
              <ul className="text-sm space-y-1">
                <li>✔ Bitcoin-like scarcity (21M cap)</li>
                <li>✔ Long-term security (40-year issuance)</li>
                <li>✔ Energy-efficient hybrid consensus</li>
                <li>✔ Predictable monetary policy</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
